/**
 * No Validation Issues Empty State
 * 
 * Displayed when all transactions are validated (success state).
 * Celebrates user progress and suggests next steps.
 * 
 * @see UX_ACTION_PLAN_ENHANCED_2025-10-13.md - Fix 5
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface NoValidationIssuesEmptyStateProps {
  transactionCount?: number;
  period?: string;
}

export const NoValidationIssuesEmptyState = ({ 
  transactionCount = 0,
  period 
}: NoValidationIssuesEmptyStateProps) => {
  const navigate = useNavigate();

  return (
    <Card className="border-validation-low bg-validation-low-bg rounded-2xl">
      <CardHeader className="text-center pb-4">
        <div className="w-20 h-20 mx-auto mb-4 bg-white border-4 border-brand-success rounded-2xl flex items-center justify-center">
          <i className="fa-solid fa-circle-check text-4xl text-brand-success"></i>
        </div>
        <CardTitle className="text-2xl font-space-grotesk text-brand-primary">
          All Clear! No Issues Found 🎉
        </CardTitle>
        <p className="text-sm text-brand-text-secondary mt-2 max-w-md mx-auto">
          {transactionCount > 0 
            ? `All ${transactionCount} transactions ${period ? `for ${period} ` : ''}have been validated and are ready for export.`
            : 'Your data quality is excellent. No validation errors to fix.'
          }
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Success Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                {transactionCount}
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Validated
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                0
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Issues
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="pt-4 pb-4 text-center">
              <div className="text-2xl font-bold font-space-grotesk text-brand-success">
                100%
              </div>
              <div className="text-xs text-brand-text-secondary mt-1">
                Quality
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="bg-brand-accent/10 border-brand-accent">
          <CardContent className="pt-4 pb-4">
            <h4 className="font-semibold text-brand-primary mb-3 flex items-center gap-2">
              <i className="fa-solid fa-arrow-right text-brand-accent"></i>
              What's Next?
            </h4>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => navigate('/review-queue')}
              >
                <i className="fa-solid fa-robot mr-2"></i>
                Review AI Suggestions
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => navigate('/exports')}
              >
                <i className="fa-solid fa-file-export mr-2"></i>
                Export to Accounting
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => navigate('/workspace')}
              >
                <i className="fa-solid fa-list-check mr-2"></i>
                Back to Workspace
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Encouragement */}
        <div className="text-center text-sm text-brand-text-secondary italic">
          Great job maintaining data quality! Keep up the excellent work. 💪
        </div>
      </CardContent>
    </Card>
  );
};
